import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { invokeTracedFunction } from "@/lib/supabase-traced";
import { logger } from "@/lib/logger";
import { Shield } from "lucide-react";
import { captureException } from "@/lib/safe-sentry";
import { motion } from "framer-motion";

interface OTPStepProps {
  onNext: (data: {}) => void;
  identifier: string; // email ou phone
  verificationType: 'email' | 'sms' | 'whatsapp';
  allowSkip?: boolean;
}

export const OTPStep = ({ onNext, identifier, verificationType, allowSkip = true }: OTPStepProps) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutos em segundos
  const { toast } = useToast();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      let functionName = 'send-otp-email';
      let payload: any = { email: identifier };
      
      if (verificationType === 'sms') {
        functionName = 'send-sms-otp';
        payload = { phone: identifier, channel: 'sms' };
      } else if (verificationType === 'whatsapp') {
        functionName = 'send-sms-otp';
        payload = { phone: identifier, channel: 'whatsapp' };
      }
      
      const { data, error } = await invokeTracedFunction(functionName, payload);

      if (error) {
        const anyErr: any = error;
        const status = anyErr?.context?.response?.status ?? anyErr?.status;
        const retryAfter = anyErr?.context?.response?.headers?.['retry-after'];
        
        if (status === 429) {
          const waitTime = retryAfter ? parseInt(retryAfter) : 3600;
          setTimeLeft(waitTime);
          
          toast({
            title: "Limite de envios excedido",
            description: `Aguarde ${formatTime(waitTime)} antes de tentar novamente.`,
            variant: "destructive"
          });
          return;
        }

        const errorMessage = data?.error || anyErr?.context?.body?.error || anyErr?.message || "Falha ao reenviar código";
        
        logger.error('Failed to resend OTP', error, { 
          component: 'OTPStep', 
          action: 'resend',
          identifier: identifier.substring(0, 3) + '***',
          type: verificationType
        });

        toast({
          title: "Erro ao reenviar código",
          description: errorMessage,
          variant: "destructive"
        });
        return;
      }

      setTimeLeft(600);
      toast({
        title: "Código reenviado!",
        description: "Verifique seu email.",
      });
    } catch (error: any) {
      logger.error('Unexpected error resending OTP', error, { 
        component: 'OTPStep', 
        action: 'resend' 
      });
      
      if (import.meta.env.PROD) {
        captureException(error);
      }

      toast({
        title: "Erro de conexão",
        description: "Verifique sua internet e tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast({
      title: "Verificação adiada",
      description: "Você poderá verificar seu email mais tarde no seu perfil.",
    });
    onNext({});
  };

  const handleVerify = async () => {
    if (otp.length !== 6 || loading) return;
    
    setLoading(true);
    try {
      let functionName = 'verify-otp';
      let payload: any = { email: identifier, code: otp };
      
      if (verificationType === 'sms' || verificationType === 'whatsapp') {
        functionName = 'verify-sms-otp';
        payload = { phone: identifier, code: otp };
      }
      
      const { data, error} = await invokeTracedFunction(functionName, payload);

      if (error) {
        const anyErr: any = error;
        const status = anyErr?.context?.response?.status ?? anyErr?.status;
        const errorData = data || anyErr?.context?.body;
        const headers = anyErr?.context?.response?.headers || {};
        const retryAfter = headers['retry-after'];
        const rateLimitRemaining = headers['x-ratelimit-remaining'];
        const functionVersion = headers['x-function-version'];
        
        logger.info('OTP verification failed', {
          component: 'OTPStep',
          status,
          retryAfter,
          rateLimitRemaining,
          functionVersion,
          correlationId: logger.getCorrelationId()
        });
        
        let errorMessage = "Verifique o código e tente novamente";
        
        if (status === 429) {
          const waitTime = retryAfter ? parseInt(retryAfter) : 60;
          setTimeLeft(waitTime);
          errorMessage = `Muitas tentativas. Aguarde ${formatTime(waitTime)}.`;
        } else if (status === 400) {
          if (errorData?.error?.includes('expirado')) {
            errorMessage = "Código expirado. Solicite um novo código.";
          } else if (errorData?.error?.includes('inválido')) {
            errorMessage = "Código inválido ou já utilizado.";
          }
        }

        logger.error('Failed to verify OTP', error, { 
          component: 'OTPStep', 
          action: 'verify',
          status,
          correlationId: logger.getCorrelationId()
        });

        toast({
          title: "Código inválido",
          description: errorMessage,
          variant: "destructive"
        });
        setOtp('');
        return;
      }

      toast({
        title: "Email verificado!",
        description: "Vamos continuar seu cadastro.",
      });

      onNext({});
    } catch (error: any) {
      logger.error('Unexpected error verifying OTP', error, { 
        component: 'OTPStep', 
        action: 'verify',
        correlationId: logger.getCorrelationId()
      });
      
      if (import.meta.env.PROD) {
        captureException(error);
      }

      toast({
        title: "Erro de conexão",
        description: "Não foi possível verificar o código. Tente novamente.",
        variant: "destructive"
      });
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6, bounce: 0.4 }}
          className="w-16 h-16 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20"
        >
          <Shield className="w-8 h-8 text-white" />
        </motion.div>
        <h1 className="text-xl font-bold">Verificação de Segurança</h1>
        <p className="text-muted-foreground">
          Enviamos um código de 6 dígitos {verificationType === 'email' ? 'para' : verificationType === 'whatsapp' ? 'via WhatsApp para' : 'via SMS para'}<br />
          <strong className="text-foreground">{identifier}</strong>
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={setOtp}
            onComplete={() => !loading && handleVerify()}
            disabled={loading}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center space-y-2"
        >
          <p className="text-sm text-muted-foreground">
            Tempo restante:{" "}
            <motion.strong
              key={timeLeft}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              className="text-primary font-mono"
            >
              {formatTime(timeLeft)}
            </motion.strong>
          </p>
          
          {timeLeft === 0 && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-destructive font-medium"
            >
              Código expirado. Solicite um novo código.
            </motion.p>
          )}
          
          {timeLeft < 3600 && timeLeft > 0 && timeLeft < 540 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-muted-foreground"
            >
              Você pode solicitar um novo código em breve
            </motion.p>
          )}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="space-y-3"
      >
        <Button
          onClick={handleVerify}
          disabled={otp.length !== 6 || loading || timeLeft === 0}
          className="w-full h-12 text-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          size="lg"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
              />
              Verificando...
            </span>
          ) : (
            "Verificar Código"
          )}
        </Button>

        <Button
          variant="ghost"
          onClick={handleResend}
          disabled={timeLeft > 540 || loading}
          className="w-full transition-all duration-200 hover:bg-primary/10"
        >
          {timeLeft > 540 ? `Aguarde ${formatTime(600 - timeLeft)}` : "Reenviar código"}
        </Button>

        {allowSkip && (
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={loading}
            className="w-full transition-all duration-200"
          >
            Verificar depois
          </Button>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-warning/10 border border-warning/30 rounded-lg p-4 text-sm text-center backdrop-blur-sm"
      >
        ⚠️ <strong>Nunca compartilhe este código com ninguém!</strong>
      </motion.div>
    </div>
  );
};